// Branch dashboard - /Home/BranchDashboard. Design: dashboard-single-branch.html.
//
// Sales, invoices and activity come from the sales report (one row per
// invoice), product-wise sale and the pay in / out report, for the branch
// picked in the app header. Profit, collections, purchases, returns and target
// come from Api/Dashboard/GetSummary; receivables, stock and the alerts from
// GetPosition / GetList; salesman targets from SalesTarget/GetAll.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { all_routes } from "../../Router/all_routes";
import {
  BarTrend,
  Card,
  DashScreen,
  DataTable,
  Delta,
  Drawer,
  Facts,
  Hero,
  MON,
  MiniBtn,
  PERIODS,
  PeriodBar,
  Queue,
  Seg,
  Sparkline,
  StackBar,
  Tile,
  cap,
  cmpRange,
  compact,
  dailyTotals,
  dayLabel,
  downloadCsv,
  fetchInvoiceSpans,
  fetchItemSales,
  fetchList,
  fetchPayInOut,
  fetchPosition,
  fetchSummary,
  fetchUserTargets,
  fmtPct,
  hourLbl,
  hourWindow,
  hourlyTotals,
  inRange,
  initials,
  listDrawer,
  longDate,
  money,
  moneyDp,
  paceCut,
  payName,
  paymentMix,
  pct,
  rangeFor,
  rankBy,
  sparkRange,
  sumOf,
  sumPosition,
  useDashKeys,
  useToast,
  useUpdatedLabel,
  valueOf,
} from "../../components/dashboard/ui";

const AGES = ["Not due / 0–30 days", "31–60 days", "61–90 days", "Over 90 days"];
const ageParts = (p, pre) =>
  ["0To30", "31To60", "61To90", "Over90"].map((k, i) => ({ n: AGES[i], v: p ? p[pre + k] || 0 : 0, c: `var(--age${i + 1})` }));

const BRANCH_PERIODS = PERIODS.filter((p) => p.id !== "qtd");
const ITEM_SORTS = [
  { id: "value", label: "By value" },
  { id: "qty", label: "By qty" },
  { id: "margin", label: "By profit" },
];
const clock = (mins) => (mins === null ? "" : `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`);

const INVOICE_COLS = [
  { k: "bill", t: "Bill no" },
  { k: "when", t: "Date / time" },
  { k: "who", t: "Cashier" },
  { k: "pay", t: "Payment" },
  { k: "amt", t: "Net total", num: true },
];
const invoiceRow = (r) => ({
  bill: r.billNo || "—",
  when: dayLabel(r.date) + (r.mins !== null ? " " + clock(r.mins) : ""),
  who: r.cashier || "—",
  pay: payName(r.payType),
  amt: moneyDp(r.net),
});

const Dashboard = () => {
  const navigate = useNavigate();
  const { branch } = useSelector((state) => state.branchlist);
  const currfmt = useSelector((state) => {
    const match = state.usersetting?.settingList?.currencyFormat?.toString()?.match(/\.0+/);
    return match ? match[0].length - 1 : 2;
  });
  const guid = branch?.guid;

  const [period, setPeriod] = useState("today");
  const [cmp, setCmp] = useState("prev");
  // on by default so the headline matches the Sales report's Net Total
  const [vatOn, setVatOn] = useState(true);
  const [itemSort, setItemSort] = useState("value");
  const [tables, setTables] = useState({});
  const [drawer, setDrawer] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stamp, setStamp] = useState(null);
  const [reload, setReload] = useState(0);
  const [toast, showToast] = useToast();
  const updated = useUpdatedLabel(stamp);
  const req = useRef(0);

  /* ---------------- load ---------------- */
  useEffect(() => {
    if (!guid) return;
    const id = ++req.current;
    const r = rangeFor(period);
    const c = cmpRange(r, cmp);
    const sp = sparkRange(r);
    setLoading(true);
    setError("");
    // the summary / position calls may fail on their own - the sales figures still show
    const soft = (p) =>
      p.catch((e) => {
        console.error("Dashboard figure failed", e?.response?.data?.message || e);
        return null;
      });
    Promise.all([
      fetchInvoiceSpans(branch, [r, c, sp]),
      fetchItemSales(branch, r.from, r.to).catch(() => []),
      fetchPayInOut(branch, r.from, r.to).catch(() => []),
      soft(fetchSummary([branch], [r, c, sp], r.n === 1)),
      soft(fetchPosition([branch])),
      soft(fetchUserTargets(branch, r.to)),
      soft(fetchList("receivables", [branch])),
    ])
      .then(([inv, items, pio, sum, pos, targets, recv]) => {
        if (id !== req.current) return;
        if (!sum || !pos) setError("Some figures (profit, stock, receivables) could not be loaded. Press Refresh to retry.");
        setData({
          r,
          c,
          sp,
          inv,
          items,
          pio,
          sum,
          pos: pos ? sumPosition(pos) : null,
          posRows: pos || [],
          targets: targets || [],
          recv: recv || [],
        });
        setStamp(Date.now());
      })
      .catch((e) => {
        if (id !== req.current) return;
        console.error("Dashboard load failed", e?.response?.data?.Message || e);
        setError("Could not load the sales figures. Check the connection and press Refresh.");
      })
      .finally(() => id === req.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid, period, cmp, reload]);

  /* ---------------- derived ---------------- */
  const v = useMemo(() => {
    if (!data) return null;
    const { r, c, sp, inv, items, pio } = data;
    const f = valueOf(vatOn);
    const cur = inRange(inv, r.from, r.to);
    const prev = paceCut(inRange(inv, c.from, c.to), c.to, r.live);
    const sales = sumOf(cur, f);
    const psales = sumOf(prev, f);
    const payIn = sumOf(pio.filter((p) => !p.out), (p) => p.amount);
    const payOut = sumOf(pio.filter((p) => p.out), (p) => p.amount);
    const cashSales = sumOf(cur, (x) => x.cash);
    const days = (from, to, g) => dailyTotals(inv, from, to, g).map((d) => d.v);
    // GetSummary - whole days, so a live period's deltas would compare part of
    // today with all of the comparison day; those tiles show no delta then
    const sdays = data.sum?.days || [];
    const sd = inRange(sdays, r.from, r.to);
    const pd = inRange(sdays, c.from, c.to);
    const s = (k) => sumOf(sd, (d) => d[k]);
    const ps = (k) => sumOf(pd, (d) => d[k]);
    const sspark = (g) => dailyTotals(sdays, sp.from, sp.to, g).map((d) => d.v);
    return {
      r,
      c,
      cur,
      prev,
      items,
      pio,
      f,
      sales,
      psales,
      inv: cur.length,
      pinv: prev.length,
      cashSales,
      payIn,
      payOut,
      cashHand: cashSales + payIn - payOut,
      pcash: sumOf(prev, (x) => x.cash),
      spark: days(sp.from, sp.to, f),
      sparkInv: days(sp.from, sp.to, () => 1),
      sparkCash: days(sp.from, sp.to, (x) => x.cash),
      firstMins: cur.reduce((m, x) => (x.mins !== null && (m === null || x.mins < m) ? x.mins : m), null),
      hasSum: !!data.sum,
      sd,
      gp: s("grossProfit"),
      pgp: ps("grossProfit"),
      salesEx: s("salesExVat"),
      coll: s("collections"),
      pcoll: ps("collections"),
      purch: s("purchases") - s("purchaseReturns"),
      ppurch: ps("purchases") - ps("purchaseReturns"),
      purchRet: s("purchaseReturns"),
      paid: s("paidToSuppliers"),
      ret: s("returnsValue"),
      pret: ps("returnsValue"),
      retN: s("returns"),
      target: s("target"),
      achieved: s("netSales"),
      sparkGp: sspark((d) => d.grossProfit),
      sparkColl: sspark((d) => d.collections),
      sparkPurch: sspark((d) => d.purchases - d.purchaseReturns),
      sparkRet: sspark((d) => d.returnsValue),
    };
  }, [data, vatOn]);

  /* ---------------- trend ---------------- */
  const trend = useMemo(() => {
    if (!v) return null;
    const { r, c, cur, prev, f } = v;
    if (r.n === 1) {
      const win = hourWindow([cur, prev], r.live);
      if (win) {
        const a = hourlyTotals(cur, win, f);
        const b = hourlyTotals(prev, win, f);
        return {
          unit: "hour",
          x: a.map((o) => hourLbl(o.h)),
          cur: a.map((o) => o.v),
          cmp: b.map((o) => o.v),
          extra: a.map((o) => o.n),
          cmpName: c.name + (r.live ? ", to this hour" : ""),
        };
      }
      // the invoices carry bare dates - use the summary's hourly split
      const hrs = data.sum?.hours || [];
      const hc = hrs.filter((o) => o.date === r.to);
      const hp = hrs.filter((o) => o.date === c.to);
      if (hc.length || hp.length) {
        const lo = Math.min(...[...hc, ...hp].map((o) => o.h));
        let hi = Math.max(...[...hc, ...hp].map((o) => o.h));
        if (r.live) hi = Math.max(hi, dayjs().hour());
        const at = (list, h, k) => sumOf(list.filter((o) => o.h === h), (o) => o[k]);
        const hs = [];
        for (let h = lo; h <= hi; h++) hs.push(h);
        return {
          unit: "hour",
          x: hs.map(hourLbl),
          cur: hs.map((h) => at(hc, h, "v")),
          cmp: hs.map((h) => at(hp, h, "v")),
          extra: hs.map((h) => at(hc, h, "n")),
          cmpName: c.name,
          fromSummary: true,
        };
      }
      // no hourly figures at all - fall back to the last 12 days
      const sp = sparkRange(r);
      const d = dailyTotals(data.inv, sp.from, sp.to, f);
      const n = dailyTotals(data.inv, sp.from, sp.to, () => 1);
      return {
        unit: "day",
        noTimes: true,
        x: d.map((o) => dayLabel(o.date)),
        cur: d.map((o) => o.v),
        cmp: [],
        extra: n.map((o) => o.v),
        cmpName: c.name,
      };
    }
    const a = dailyTotals(cur, r.from, r.to, f);
    const b = dailyTotals(prev, c.from, c.to, f);
    const n = dailyTotals(cur, r.from, r.to, () => 1);
    return {
      unit: "day",
      x: a.map((o) => dayLabel(o.date)),
      cur: a.map((o) => o.v),
      cmp: b.map((o) => o.v),
      extra: n.map((o) => o.v),
      cmpName: c.name,
    };
  }, [v, data]);

  /* ---------------- drawer ---------------- */
  const openInvoices = useCallback(
    (title, rows, note) =>
      setDrawer({
        title,
        sub: `${rows.length} invoices · ${branch?.name || ""}`,
        note,
        cols: INVOICE_COLS,
        rows: rows.slice().sort((a, b) => (b.date + clock(b.mins)).localeCompare(a.date + clock(a.mins))).map(invoiceRow),
      }),
    [branch]
  );
  const drawerCsv = (d) => {
    downloadCsv(
      `${d.title}.csv`.replace(/[\\/:*?"<>|]/g, "-"),
      d.cols.map((c) => c.t),
      d.rows.map((row) => d.cols.map((c) => row[c.k]))
    );
    showToast("List exported");
  };
  // GetList rows behind an alert, fetched when opened
  const listReq = useRef(0);
  const openList = (kind) => {
    const id = ++listReq.current;
    setDrawer({ ...listDrawer(kind, [], "Loading…", ["br"]), note: "Loading…" });
    fetchList(kind, [branch])
      .then((rows) => id === listReq.current && setDrawer(listDrawer(kind, rows, `${rows.length} rows · ${branch?.name || ""}`, ["br"])))
      .catch((e) => {
        if (id !== listReq.current) return;
        setDrawer(null);
        showToast(e?.response?.data?.message || "Could not load the list");
      });
  };
  // one GetSummary measure, day by day over the period
  const openDays = (title, cols) =>
    v &&
    setDrawer({
      title,
      sub: `${cap(v.r.name)} · ${branch?.name || ""}`,
      cols: [{ k: "d", t: "Date" }, ...cols.map(([k, t]) => ({ k, t, num: true }))],
      rows: [
        ...v.sd.map((d) => ({ d: dayLabel(d.date), ...Object.fromEntries(cols.map(([k, , f]) => [k, moneyDp(f(d))])) })),
        { __total: true, d: "Total", ...Object.fromEntries(cols.map(([k, , f]) => [k, moneyDp(sumOf(v.sd, f))])) },
      ],
    });

  /* ---------------- handlers ---------------- */
  const refresh = () => {
    setReload((x) => x + 1);
    showToast("Figures refreshed");
  };
  const pickPeriod = (id) => BRANCH_PERIODS.some((p) => p.id === id) && setPeriod(id);
  const toggleTable = (k) => setTables((t) => ({ ...t, [k]: !t[k] }));
  const exportCsv = () => {
    if (!trend) return;
    downloadCsv(
      `axoone-${String(branch?.name || "branch").toLowerCase().replace(/\s+/g, "-")}-${period}.csv`,
      [trend.unit === "hour" ? "Hour" : "Date", "Sales", "Comparison", "Invoices"],
      trend.x.map((s, i) => [s, (trend.cur[i] || 0).toFixed(2), (trend.cmp[i] || 0).toFixed(2), trend.extra[i]])
    );
    showToast("CSV downloaded");
  };

  useDashKeys({ onPeriod: pickPeriod, onRefresh: refresh, onEscape: () => setDrawer(null) });

  /* ---------------- render ---------------- */
  const r = v?.r || rangeFor(period);
  const c = v?.c || cmpRange(r, cmp);
  const title = branch?.name || "Branch dashboard";
  const sub = !guid
    ? "Pick a branch from the store menu in the header"
    : period === "today"
      ? longDate(r.to) + (v?.firstMins != null ? ` · trading since ${clock(v.firstMins)}` : "")
      : `${cap(r.name)} · ${r.n === 1 ? longDate(r.to) : dayLabel(r.from) + " → " + dayLabel(r.to)}`;

  // payment mix
  const mix = v ? paymentMix(v.cur, vatOn) : [];
  const mixTotal = mix.reduce((a, m) => a + m.v, 0);

  // GetSummary tiles; no delta while the period is live (whole comparison days)
  const sv = (x) => (v?.hasSum ? compact(x || 0) : "—");
  const sdelta = (cur, prev, invert) => (v?.hasSum && !v.r.live ? <Delta cur={cur} prev={prev} invert={invert} small /> : null);

  // top items
  const itemKey = itemSort === "qty" ? "qty" : itemSort === "margin" ? "profit" : "value";
  const itemRows = (v?.items || [])
    .slice()
    .sort((a, b) => b[itemKey] - a[itemKey])
    .slice(0, 7);
  const itemMax = itemRows[0] ? Math.max(itemRows[0][itemKey], 1) : 1;

  // customers / cashiers
  const custRows = v ? rankBy(v.cur, (x) => x.customer, v.f).slice(0, 6) : [];
  const owes = {};
  (data?.recv || []).forEach((x) => {
    if (x.partyName) owes[x.partyName] = (owes[x.partyName] || 0) + (Number(x.balance) || 0);
  });
  const cashierRows = v ? rankBy(v.cur, (x) => x.cashier, v.f).slice(0, 6) : [];
  const cashierMax = cashierRows[0]?.v || 1;
  const userTargets = (data?.targets || []).filter((t) => Number(t.amount) > 0);

  // position (GetPosition, as on today)
  const p = data?.pos || null;
  const recvParts = ageParts(p, "receivable");
  const payParts = ageParts(p, "payable");
  // day close: only POS branches have a business date
  const posRow = data?.posRows?.[0] || null;
  const dayBehind = !!(posRow?.businessDate && !posRow.dayClosedUpToYesterday);

  // needs attention
  const pending = v ? v.cur.filter((x) => x.status === 1) : [];
  const credit = v ? v.cur.filter((x) => x.payType === 4) : [];
  const outs = v ? v.pio.filter((p) => p.out) : [];
  const queue = [
    {
      key: "pending",
      sev: pending.length ? "warnx" : "info",
      ic: "doc",
      t: pending.length ? "Bills still pending" : "No pending bills",
      s: pending.length ? "raised but not completed" : "every bill is completed",
      n: pending.length ? `${pending.length} bills` : "All clear",
    },
    {
      key: "credit",
      sev: "info",
      ic: "card",
      t: "Sold on credit",
      s: `${credit.length} invoices on account ${r.name}`,
      n: compact(sumOf(credit, v ? v.f : () => 0)),
    },
    {
      key: "payout",
      sev: "info",
      ic: "cash",
      t: "Cash paid out of the drawer",
      s: `${outs.length} pay-outs ${r.name}`,
      n: compact(v?.payOut || 0),
    },
    ...(dayBehind
      ? [
          {
            key: "dayclose",
            kind: "dayclose",
            sev: "warnx",
            ic: "lock",
            t: "Day not closed",
            s: `business date ${dayLabel(posRow.businessDate)} · cash and stock unreconciled`,
            n: posRow.lastDayClosed ? `closed to ${dayLabel(posRow.lastDayClosed)}` : "never closed",
          },
        ]
      : []),
    // GetPosition counts; each opens its GetList rows
    ...[
      ["ar90", "ar90", "crit", "clock", "Overdue receivables past 90 days", "customersOver90", "customers", () => `${compact(p.receivableOver90)} overdue`],
      ["oos", "oos", "crit", "box", "Items out of stock", "outOfStockItems", "items", () => "sales blocked on the counter"],
      ["grn", "grn", "warnx", "truck", "Goods receipts awaiting purchase invoice", "goodsReceiptsNotInvoiced", "GRNs", () => `${compact(p.goodsReceiptsNotInvoicedValue)} unbilled`],
      ["dn", "dn", "warnx", "doc", "Delivery notes not yet invoiced", "deliveryNotesNotInvoiced", "DNs", () => `${compact(p.deliveryNotesNotInvoicedValue)} goods gone, no bill`],
      ["limit", "credit", "warnx", "card", "Customers over their credit limit", "customersOverLimit", "customers", () => `${compact(p.overLimitAmount)} over the limits`],
      ["reorder", "reorder", "warnx", "box", "Items below reorder level", "belowReorderItems", "items", () => "fast movers first"],
      ["quote", "quote", "info", "quote", "Quotations expiring within 3 days", "quotationsExpiring3Days", "quotes", () => `${compact(p.quotationsExpiringValue)} worth chasing`],
      ["po", "po", "info", "po", "Purchase orders overdue from supplier", "overduePurchaseOrders", "POs", () => `${p.openPurchaseOrders || 0} open · promised date passed`],
    ].map(([key, kind, sev, ic, t, k, unit, s]) => {
      const n = p ? p[k] || 0 : null;
      return {
        key,
        kind: n ? kind : null,
        sev: n ? sev : "info",
        ic,
        t,
        s: p ? s() : "could not be loaded",
        n: n === null ? "—" : n ? `${money(n)} ${unit}` : "All clear",
      };
    }),
    {
      key: "wast",
      kind: p && p.damageValue30 + p.wastageValue30 ? "wastage" : null,
      sev: "info",
      ic: "warn",
      t: "Damage & wastage, last 30 days",
      s: p ? `damage ${compact(p.damageValue30)} · wastage ${compact(p.wastageValue30)}` : "could not be loaded",
      n: p ? compact(p.damageValue30 + p.wastageValue30) : "—",
    },
  ];
  const onQueue = (x) => {
    if (x.kind) openList(x.kind);
    else if (x.key === "pending") openInvoices("Bills still pending", pending);
    else if (x.key === "credit") openInvoices("Sold on credit", credit);
    else if (x.key === "payout")
      setDrawer({
        title: "Cash paid out",
        sub: `${outs.length} pay-outs · ${branch?.name || ""}`,
        cols: [
          { k: "when", t: "Date / time" },
          { k: "acc", t: "Account" },
          { k: "nar", t: "Narration" },
          { k: "amt", t: "Amount", num: true },
        ],
        rows: outs.map((p) => ({
          when: dayLabel(p.date) + (p.mins !== null ? " " + clock(p.mins) : ""),
          acc: p.account || "—",
          nar: p.narration || "—",
          amt: moneyDp(p.amount),
        })),
      });
  };

  // live activity - invoices and pay in / out, newest first
  const feed = v
    ? [
        ...v.cur.map((x) => ({
          k: "i" + x.id,
          tg: x.payType === 4 ? "CRED" : "SALE",
          cls: "sale",
          doc: x.billNo || "—",
          who: x.customer || x.cashier || x.counter,
          amt: v.f(x),
          date: x.date,
          mins: x.mins,
          inv: x,
        })),
        ...v.pio.map((p) => ({
          k: "p" + p.id,
          tg: p.out ? "PAY" : "RCPT",
          cls: p.out ? "pay" : "rcpt",
          doc: p.out ? "Pay out" : "Pay in",
          who: p.account || p.narration,
          amt: p.amount,
          date: p.date,
          mins: p.mins,
        })),
      ]
        .sort((a, b) => (b.date + clock(b.mins)).localeCompare(a.date + clock(a.mins)))
        .slice(0, 20)
    : [];
  const today = dayjs().format("YYYY-MM-DD");

  const drawerEl = (
    <Drawer
      data={drawer}
      onClose={() => setDrawer(null)}
      onCsv={drawerCsv}
      primary={
        <button type="button" className="axd-btn primary" onClick={() => setDrawer(null)}>
          Done
        </button>
      }
    />
  );

  return (
    <DashScreen
      title={title}
      sub={sub}
      updated={updated}
      loading={loading}
      error={error}
      drawer={drawerEl}
      toast={toast}
      keys={[
        ["T", "Today"],
        ["W", "Week"],
        ["M", "Month"],
        ["R", "Refresh"],
        ["Esc", "Close"],
      ]}
      status={`${branch?.name || "No branch"} · ${vatOn ? "incl. VAT" : "excl. VAT"} · ${r.n} ${r.n === 1 ? "day" : "days"}`}
    >
      <PeriodBar
        periods={BRANCH_PERIODS}
        period={period}
        onPeriod={setPeriod}
        cmp={cmp}
        onCmp={setCmp}
        vat={vatOn}
        onVat={setVatOn}
        vatLabel="Show amounts with VAT"
        onRefresh={refresh}
        onCsv={exportCsv}
        onPrint={() => window.print()}
      />

      {/* ============ KPI ROW ============ */}
      <section className="axd-kpirow">
        <Hero
          label={`Net sales · ${r.name}`}
          value={money(v?.sales || 0)}
          loading={!v && loading}
          prev={v ? { cur: v.sales, prev: v.psales } : undefined}
          cmpName={c.name}
          spark={<Sparkline vals={v?.spark || []} w={256} h={40} />}
          footLabel={r.live ? "Target to now" : "Target"}
          foot={<b>{v?.target ? money(v.target) : "—"}</b>}
          footRight={
            !v ? null : v.target ? (
              <b style={{ color: v.achieved >= v.target ? "var(--money)" : undefined }}>{fmtPct(pct(v.achieved, v.target), 0)} achieved</b>
            ) : (
              v.hasSum && "no target set"
            )
          }
        />
        <div className="axd-tiles">
          <Tile
            label="Gross profit"
            value={sv(v?.gp)}
            delta={sdelta(v?.gp, v?.pgp)}
            sub={`margin ${fmtPct(pct(v?.gp || 0, v?.salesEx || 0))} · ex VAT`}
            spark={<Sparkline vals={v?.sparkGp || []} />}
            onClick={() =>
              openDays("Gross profit", [
                ["s", "Sales ex VAT", (d) => d.salesExVat],
                ["c", "Cost", (d) => d.cost],
                ["g", "Gross profit", (d) => d.grossProfit],
              ])
            }
          />
          <Tile
            label="Invoices"
            value={money(v?.inv || 0)}
            delta={v ? <Delta cur={v.inv} prev={v.pinv} small /> : null}
            sub={`avg bill ${moneyDp(v && v.inv ? v.sales / v.inv : 0, currfmt)}`}
            spark={<Sparkline vals={v?.sparkInv || []} />}
            onClick={() => v && openInvoices(`Invoices · ${r.name}`, v.cur)}
          />
          <Tile
            label="Collections"
            value={sv(v?.coll)}
            delta={sdelta(v?.coll, v?.pcoll)}
            sub="cash & card in, incl. receipts"
            spark={<Sparkline vals={v?.sparkColl || []} />}
            onClick={() =>
              openDays("Collections", [
                ["b", "Bill receipts", (d) => d.billReceipts],
                ["r", "Receipts", (d) => d.receipts],
                ["t", "Collections", (d) => d.collections],
              ])
            }
          />
          <Tile
            label="Purchases"
            value={sv(v?.purch)}
            delta={sdelta(v?.purch, v?.ppurch)}
            sub={`net of returns · paid ${compact(v?.paid || 0)}`}
            spark={<Sparkline vals={v?.sparkPurch || []} />}
            onClick={() =>
              openDays("Purchases", [
                ["p", "Purchases", (d) => d.purchases],
                ["r", "Returns", (d) => d.purchaseReturns],
                ["s", "Paid to suppliers", (d) => d.paidToSuppliers],
              ])
            }
          />
          <Tile
            label="Sales returns"
            value={sv(v?.ret)}
            delta={sdelta(v?.ret, v?.pret, true)}
            sub={`${money(v?.retN || 0)} returns`}
            spark={<Sparkline vals={v?.sparkRet || []} />}
            onClick={() => openDays("Sales returns", [["r", "Returns value", (d) => d.returnsValue]])}
          />
          <Tile
            label="Cash in hand"
            value={compact(v?.cashHand || 0)}
            delta={v ? <Delta cur={v.cashSales} prev={v.pcash} small /> : null}
            sub="cash sales + pay in − pay out"
            spark={<Sparkline vals={v?.sparkCash || []} />}
            onClick={() =>
              v &&
              openInvoices(
                `Cash sales · ${r.name}`,
                v.cur.filter((x) => x.cash),
                `Cash sales ${moneyDp(v.cashSales)} + pay in ${moneyDp(v.payIn)} − pay out ${moneyDp(v.payOut)}. The opening float is not included.`
              )
            }
          />
        </div>
      </section>

      {/* ============ MAIN GRID ============ */}
      <section className="axd-g12">
        {/* trend */}
        <Card
          className="c8"
          title={trend?.unit === "hour" ? "Sales through the day" : "Sales day by day"}
          note={
            trend?.noTimes
              ? "last 12 days · no hourly figures"
              : trend?.unit === "hour"
                ? trend.fromSummary
                  ? "hourly · after returns"
                  : "hourly"
                : `${trend?.x.length || 0} days`
          }
          right={
            <MiniBtn on={tables.trend} onClick={() => toggleTable("trend")}>
              {tables.trend ? "Chart" : "Table"}
            </MiniBtn>
          }
          bodyClass="col"
        >
          {!trend ? (
            <div className="axd-nochart axd-empty">{guid ? "Loading…" : "No branch selected."}</div>
          ) : tables.trend ? (
            <DataTable
              cols={[
                { k: "x", t: trend.unit === "hour" ? "Hour" : "Date" },
                { k: "a", t: "Sales", num: true },
                { k: "b", t: trend.cmpName, num: true },
                { k: "d", t: "Diff", num: true },
                { k: "n", t: "Invoices", num: true },
              ]}
              rows={trend.x.map((s, i) => {
                const df = (trend.cur[i] || 0) - (trend.cmp[i] || 0);
                return {
                  x: s,
                  a: money(trend.cur[i]),
                  b: trend.cmp.length ? money(trend.cmp[i]) : "—",
                  d: trend.cmp.length ? (
                    <span style={{ color: df >= 0 ? "var(--money)" : "var(--danger)" }}>
                      {(df >= 0 ? "+" : "") + money(df)}
                    </span>
                  ) : (
                    "—"
                  ),
                  n: trend.extra[i],
                };
              })}
            />
          ) : (
            <BarTrend x={trend.x} cur={trend.cur} cmp={trend.cmp} extra={trend.extra} cmpName={trend.cmpName} />
          )}
          {trend && (
            <div className="axd-legend">
              <span className="li">
                <span className="sw" style={{ background: "var(--accent)" }} />
                {trend.noTimes ? "Last 12 days" : cap(r.name.replace(/^the /, ""))}
              </span>
              {trend.cmp.length > 0 && (
                <span className="li">
                  <span className="ln" style={{ background: "var(--ghost)" }} />
                  {trend.cmpName}
                </span>
              )}
              <span className="li" style={{ color: "var(--ink-3)" }}>
                hover for the exact figure
              </span>
            </div>
          )}
        </Card>

        {/* needs attention */}
        <Card
          className="c4"
          title="Needs attention"
          note={`${queue.filter((x) => x.sev !== "info").length} need action`}
        >
          <Queue items={queue} onPick={onQueue} />
        </Card>

        {/* payment mix */}
        <Card
          className="c4"
          title="How customers paid"
          right={
            <MiniBtn on={tables.pay} onClick={() => toggleTable("pay")}>
              {tables.pay ? "Chart" : "Table"}
            </MiniBtn>
          }
        >
          {tables.pay ? (
            <DataTable
              cols={[
                { k: "n", t: "Tender" },
                { k: "v", t: "Amount", num: true },
                { k: "p", t: "Share", num: true },
              ]}
              rows={[
                ...mix.map((m) => ({ n: m.n, v: moneyDp(m.v), p: fmtPct(pct(m.v, mixTotal)) })),
                { __total: true, n: "Total", v: moneyDp(mixTotal), p: "100.0%" },
              ]}
            />
          ) : (
            <>
              <StackBar parts={mix} />
              <div className="axd-stkey">
                {mix.map((m) => (
                  <span className="k" key={m.n}>
                    <span className="sw" style={{ background: m.c }} />
                    <span className="kn">{m.n}</span>
                    <span className="kv">{money(m.v)}</span>
                    <span className="kp">{fmtPct(pct(m.v, mixTotal), 0)}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* top items */}
        <Card
          className="c4"
          title="Top items"
          right={<Seg small items={ITEM_SORTS} value={itemSort} onChange={setItemSort} />}
        >
          <div className="axd-blist">
            {itemRows.length ? (
              itemRows.map((x) => (
                <button
                  type="button"
                  className="axd-brow"
                  key={x.name}
                  onClick={() =>
                    setDrawer({
                      title: x.name,
                      sub: `${cap(r.name)} · ${branch?.name || ""}`,
                      cols: [
                        { k: "d", t: "Date" },
                        { k: "q", t: "Qty", num: true },
                        { k: "v", t: "Value", num: true },
                      ],
                      rows: Object.keys(x.days)
                        .sort()
                        .reverse()
                        .map((d) => ({ d: dayLabel(d), q: x.days[d].qty, v: moneyDp(x.days[d].value) })),
                    })
                  }
                >
                  <span>
                    <span className="nm">{x.name}</span>
                    <span className="track">
                      <i style={{ width: Math.max(0, pct(x[itemKey], itemMax)).toFixed(1) + "%" }} />
                    </span>
                  </span>
                  <span className="v">
                    {itemSort === "qty" ? money(x.qty) : compact(x[itemKey])}
                    <small>
                      {itemSort === "qty"
                        ? "units"
                        : itemSort === "margin"
                          ? `${fmtPct(pct(x.profit, x.net), 0)} margin`
                          : `${money(x.qty)} u`}
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <div className="axd-empty">No item sales in this period.</div>
            )}
          </div>
        </Card>

        {/* top customers */}
        <Card
          className="c4"
          title="Top customers"
          right={
            <MiniBtn onClick={() => openList("receivables")} title="Every customer balance">
              Balances
            </MiniBtn>
          }
        >
          <div className="axd-blist">
            {custRows.length ? (
              custRows.map((x) => (
                <button
                  type="button"
                  className="axd-brow"
                  key={x.name}
                  onClick={() => openInvoices(x.name, v.cur.filter((i) => i.customer === x.name))}
                >
                  <span>
                    <span className="nm">{x.name}</span>
                    <span className="track">
                      <i style={{ width: pct(x.v, custRows[0].v).toFixed(1) + "%" }} />
                    </span>
                  </span>
                  <span className="v">
                    {compact(x.v)}
                    <small className={owes[x.name] ? "warn" : undefined}>
                      {owes[x.name] ? `owes ${compact(owes[x.name])}` : `${x.n} ${x.n === 1 ? "invoice" : "invoices"}`}
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <div className="axd-empty">No sales to named customers in this period.</div>
            )}
          </div>
        </Card>

        {/* receivables */}
        <Card
          className="c4"
          title="Money owed to us"
          note={p ? `${compact(p.receivableTotal)} as on today` : ""}
          right={
            <MiniBtn onClick={() => openList("receivables")} title="Customer balances by age">
              List
            </MiniBtn>
          }
        >
          {p ? (
            <>
              <StackBar parts={recvParts} />
              <div className="axd-caption">Age bands</div>
              <div className="axd-stkey">
                {recvParts.map((x) => (
                  <span className="k" key={x.n}>
                    <span className="sw" style={{ background: x.c }} />
                    <span className="kn">{x.n}</span>
                    <span className="kv">{money(x.v)}</span>
                    <span className="kp">{fmtPct(pct(x.v, p.receivableTotal), 0)}</span>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Receivables could not be loaded."}</div>
          )}
        </Card>

        {/* salesmen */}
        <Card
          className="c4"
          title="Salesman vs target"
          note={userTargets.length ? `${MON[dayjs(r.to).month()]} targets` : cashierRows.length ? "share of sales · no targets set" : ""}
          right={
            <MiniBtn onClick={() => navigate(all_routes.salestarget)} title="Set branch and salesman targets">
              Targets
            </MiniBtn>
          }
        >
          {userTargets.length ? (
            userTargets.map((t) => {
              const a = Number(t.achievedPct) || pct(Number(t.achieved) || 0, Number(t.amount) || 0);
              return (
                <div className="axd-mrow" key={t.guid || t.userID}>
                  <span>
                    <span className="nm">
                      <span className="av">{initials(t.userName)}</span>
                      {t.userName || "—"}
                    </span>
                    <span className="axd-meter">
                      <i style={{ width: Math.min(100, a).toFixed(1) + "%" }} />
                    </span>
                  </span>
                  <span className="pct">
                    {fmtPct(a, 0)}
                    <small>
                      {compact(t.achieved)} of {compact(t.amount)}
                    </small>
                  </span>
                </div>
              );
            })
          ) : cashierRows.length ? (
            cashierRows.map((x) => (
              <button
                type="button"
                className="axd-mrow"
                key={x.name}
                onClick={() => openInvoices(x.name, v.cur.filter((i) => i.cashier === x.name))}
              >
                <span>
                  <span className="nm">
                    <span className="av">{initials(x.name)}</span>
                    {x.name}
                    <em>· {x.n} inv</em>
                  </span>
                  <span className="axd-meter">
                    <i style={{ width: pct(x.v, cashierMax).toFixed(1) + "%" }} />
                  </span>
                </span>
                <span className="pct">
                  {fmtPct(pct(x.v, v.sales), 0)}
                  <small>{compact(x.v)} of sales</small>
                </span>
              </button>
            ))
          ) : (
            <div className="axd-empty">No sales in this period.</div>
          )}
        </Card>

        {/* stock */}
        <Card
          className="c4"
          title="Stock health"
          note="as on today"
          right={
            <MiniBtn onClick={() => openList("deadstock")} title="Items with no sale in 90 days">
              Dead stock
            </MiniBtn>
          }
        >
          {p ? (
            <Facts
              cols={2}
              items={[
                { l: "Stock value", v: compact(p.stockValue), s: `${money(p.itemsInStock)} items in stock` },
                { l: "Cover", v: p.stockCoverDays ? `${Math.round(p.stockCoverDays)} days` : "—", s: "at the last 30 days' pace" },
                { l: "Out of stock", v: money(p.outOfStockItems), s: `${money(p.belowReorderItems)} below reorder`, cls: p.outOfStockItems ? "warn" : "" },
                { l: "Negative stock", v: money(p.negativeItems), s: "items below zero", cls: p.negativeItems ? "bad" : "" },
                { l: "Dead stock", v: compact(p.deadStockValue), s: `${money(p.deadStockItems)} items, no sale in 90 days` },
                { l: "Damage & wastage", v: compact(p.damageValue30 + p.wastageValue30), s: "last 30 days" },
              ]}
            />
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Stock figures could not be loaded."}</div>
          )}
        </Card>

        {/* purchase & payables */}
        <Card
          className="c7"
          title="Purchase & supplier position"
          note={cap(r.name)}
          right={
            <>
              <MiniBtn onClick={() => openList("po")}>Open POs</MiniBtn>
              <MiniBtn onClick={() => openList("payables")}>Payables</MiniBtn>
            </>
          }
          bodyClass="col"
        >
          {v?.hasSum || p ? (
            <>
              <Facts
                items={[
                  { l: "Purchases", v: sv(v?.purch), s: `returns ${compact(v?.purchRet || 0)}` },
                  { l: "Paid to suppliers", v: sv(v?.paid), s: cap(r.name) },
                  { l: "Open purchase orders", v: p ? money(p.openPurchaseOrders) : "—", s: p ? `${compact(p.openPurchaseOrderValue)} · ${money(p.overduePurchaseOrders)} overdue` : "" },
                  { l: "We owe", v: p ? compact(p.payableTotal) : "—", s: "supplier balances today" },
                ]}
              />
              {p ? (
                <>
                  <StackBar parts={payParts} thin />
                  <div className="axd-stkey">
                    {payParts.map((x) => (
                      <span className="k" key={x.n}>
                        <span className="sw" style={{ background: x.c }} />
                        <span className="kn">{x.n}</span>
                        <span className="kv">{money(x.v)}</span>
                      </span>
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Purchase figures could not be loaded."}</div>
          )}
        </Card>

        {/* activity */}
        <Card className="c5" title="Live activity" note={`last ${feed.length} documents`}>
          <div className="axd-feed">
            {feed.length ? (
              feed.map((x) => (
                <button
                  type="button"
                  className="axd-frow"
                  key={x.k}
                  onClick={() =>
                    x.inv
                      ? setDrawer({
                          title: `Invoice ${x.inv.billNo}`,
                          sub: `${dayLabel(x.inv.date)} ${clock(x.inv.mins)} · ${branch?.name || ""}`,
                          cols: [
                            { k: "m", t: "Field" },
                            { k: "v", t: "Value", num: true },
                          ],
                          rows: [
                            { m: "Gross total", v: moneyDp(x.inv.gross) },
                            { m: "Discount", v: moneyDp(x.inv.discount) },
                            { m: "VAT", v: moneyDp(x.inv.vat) },
                            { m: "Net total", v: moneyDp(x.inv.net) },
                            { m: "Cash", v: moneyDp(x.inv.cash) },
                            { m: "Card", v: moneyDp(x.inv.card) },
                            { m: "Payment", v: payName(x.inv.payType) },
                            { m: "Cashier", v: x.inv.cashier || "—" },
                            { m: "Counter", v: x.inv.counter || "—" },
                            { m: "Customer", v: x.inv.customer || "—" },
                          ],
                        })
                      : showToast(`${x.doc} · ${moneyDp(x.amt)}`)
                  }
                >
                  <span className={"axd-tg " + x.cls}>{x.tg}</span>
                  <span className="fn">
                    <b style={{ fontWeight: 600 }}>{x.doc}</b> <em>{x.who}</em>
                  </span>
                  <span className="fv">{money(x.amt)}</span>
                  <span className="ft">{x.date === today && x.mins !== null ? clock(x.mins) : dayLabel(x.date)}</span>
                </button>
              ))
            ) : (
              <div className="axd-empty">Nothing posted in this period yet.</div>
            )}
          </div>
        </Card>
      </section>
    </DashScreen>
  );
};

export default Dashboard;
